/**
 * React Query Hooks for Trading Strategies
 *
 * Provides hooks for fetching and managing trading strategies with
 * automatic caching, retry logic, and optimistic updates.
 */

import { useQuery, useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { strategyAPI } from '../../services/tradingBotAPI';
import { queryKeys, staleTimeConfig } from '../../config/queryClient';
import type { Strategy, StrategyConfig } from '../../types';

/**
 * Fetch all strategies
 *
 * @param filters - Optional filter string
 * @returns Query with strategies list
 */
export function useStrategies(filters?: string) {
  return useQuery({
    queryKey: queryKeys.strategies.list(filters),
    queryFn: async () => {
      const response = await strategyAPI.getAll();
      return response.data.data as Strategy[];
    },
    staleTime: staleTimeConfig.frequent, // 2 minutes
  });
}

/**
 * Fetch single strategy by ID
 *
 * @param id - Strategy ID
 * @returns Query with strategy details
 */
export function useStrategy(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.strategies.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Strategy ID is required');
      const response = await strategyAPI.getById(id);
      return response.data.data as Strategy;
    },
    enabled: !!id, // Only fetch if id is provided
    staleTime: staleTimeConfig.frequent,
  });
}

/**
 * Create a new strategy
 *
 * @returns Mutation for creating strategy
 */
export function useCreateStrategy(): UseMutationResult<Strategy, Error, StrategyConfig> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: StrategyConfig) => {
      const response = await strategyAPI.create(config);
      return response.data.data as Strategy;
    },
    onSuccess: () => {
      // Invalidate strategies list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.strategies.all });
    },
    onError: (error) => {
      console.error('Failed to create strategy:', error);
    },
  });
}

/**
 * Update an existing strategy
 *
 * @returns Mutation for updating strategy
 */
export function useUpdateStrategy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, config }: { id: string; config: Partial<StrategyConfig> }) => {
      const response = await strategyAPI.update(id, config);
      return response.data.data as Strategy;
    },
    onSuccess: (data) => {
      // Invalidate specific strategy and list
      queryClient.invalidateQueries({ queryKey: queryKeys.strategies.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.strategies.lists() });
    },
  });
}

/**
 * Delete a strategy
 *
 * @returns Mutation for deleting strategy
 */
export function useDeleteStrategy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await strategyAPI.delete(id);
      return id;
    },
    onSuccess: () => {
      // Invalidate strategies list
      queryClient.invalidateQueries({ queryKey: queryKeys.strategies.all });
    },
  });
}

/**
 * Activate a strategy with optimistic update
 *
 * @returns Mutation for activating strategy
 */
export function useActivateStrategy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await strategyAPI.activate(id);
      return id;
    },
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.strategies.lists() });

      // Snapshot previous value
      const previousStrategies = queryClient.getQueryData(queryKeys.strategies.lists());

      // Optimistically update to the new value
      queryClient.setQueryData(queryKeys.strategies.lists(), (old: Strategy[] | undefined) => {
        if (!old) return old;
        return old.map((strategy) =>
          strategy.id === id ? { ...strategy, status: 'ACTIVE' as const } : strategy
        );
      });

      return { previousStrategies };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousStrategies) {
        queryClient.setQueryData(queryKeys.strategies.lists(), context.previousStrategies);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.strategies.all });
    },
  });
}

/**
 * Pause a strategy with optimistic update
 *
 * @returns Mutation for pausing strategy
 */
export function usePauseStrategy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await strategyAPI.pause(id);
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.strategies.lists() });
      const previousStrategies = queryClient.getQueryData(queryKeys.strategies.lists());

      queryClient.setQueryData(queryKeys.strategies.lists(), (old: Strategy[] | undefined) => {
        if (!old) return old;
        return old.map((strategy) =>
          strategy.id === id ? { ...strategy, status: 'PAUSED' as const } : strategy
        );
      });

      return { previousStrategies };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousStrategies) {
        queryClient.setQueryData(queryKeys.strategies.lists(), context.previousStrategies);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.strategies.all });
    },
  });
}

/**
 * Stop a strategy with optimistic update
 *
 * @returns Mutation for stopping strategy
 */
export function useStopStrategy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await strategyAPI.stop(id);
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.strategies.lists() });
      const previousStrategies = queryClient.getQueryData(queryKeys.strategies.lists());

      queryClient.setQueryData(queryKeys.strategies.lists(), (old: Strategy[] | undefined) => {
        if (!old) return old;
        return old.map((strategy) =>
          strategy.id === id ? { ...strategy, status: 'STOPPED' as const } : strategy
        );
      });

      return { previousStrategies };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousStrategies) {
        queryClient.setQueryData(queryKeys.strategies.lists(), context.previousStrategies);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.strategies.all });
    },
  });
}
